import React, { useRef, useState, useEffect } from "react";

export default function PunchIn() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const detectCanvasRef = useRef(null);

  const [streamActive, setStreamActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const [faceSupported, setFaceSupported] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [detectLoading, setDetectLoading] = useState(false);

  const [punchHistory, setPunchHistory] = useState([]);
  const [takingPhoto, setTakingPhoto] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);

  const [cameraAllowed, setCameraAllowed] = useState(null);
  const [locationAllowed, setLocationAllowed] = useState(null);
  const [permissionPopup, setPermissionPopup] = useState(false);

  const [showClearModal, setShowClearModal] = useState(false);

  const detectIntervalRef = useRef(null);

  // ------------------------------------------------------------
  // Check camera + location permissions
  // ------------------------------------------------------------
  const checkPermissions = async () => {
    try {
      const cam = await navigator.permissions.query({ name: "camera" });
      setCameraAllowed(cam.state === "granted");
    } catch {
      setCameraAllowed(null);
    }

    try {
      const loc = await navigator.permissions.query({ name: "geolocation" });
      setLocationAllowed(loc.state === "granted");
    } catch {
      setLocationAllowed(null);
    }
  };

  useEffect(() => {
    setFaceSupported("FaceDetector" in window);
    checkPermissions();
    return () => stopCamera();
  }, []);

  // ------------------------------------------------------------
  // Start camera preview (SAFELY)
  // ------------------------------------------------------------
  const openCameraPreview = async () => {
    setCameraError(null);

    try {
      // TRY 1: front camera
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });

      videoRef.current.srcObject = stream;
      await videoRef.current.play().catch(() => {});
      setStreamActive(true);
      startFaceDetectionLoop();
      return;
    } catch {
      console.warn("Front camera failed");
    }

    try {
      // TRY 2: any camera
      const stream2 = await navigator.mediaDevices.getUserMedia({ video: true });

      videoRef.current.srcObject = stream2;
      await videoRef.current.play().catch(() => {});
      setStreamActive(true);
      startFaceDetectionLoop();
      return;
    } catch {
      console.warn("Fallback camera failed");
    }

    setCameraError("Unable to open camera. Close Zoom/Teams/Meet/etc.");
  };

  // ------------------------------------------------------------
  // Stop camera
  // ------------------------------------------------------------
  const stopCamera = () => {
    const v = videoRef.current;
    if (v && v.srcObject) {
      v.srcObject.getTracks().forEach((t) => t.stop());
      v.srcObject = null;
    }

    setStreamActive(false);
    clearInterval(detectIntervalRef.current);
    detectIntervalRef.current = null;
    setFaceDetected(false);
  };

  // ------------------------------------------------------------
  // Face detection loop
  // ------------------------------------------------------------
  const startFaceDetectionLoop = () => {
    if (!faceSupported) {
      setFaceDetected(true);
      return;
    }

    const detector = new window.FaceDetector();

    detectIntervalRef.current = setInterval(async () => {
      const v = videoRef.current;
      const c = detectCanvasRef.current;

      if (!v || v.readyState < 2) return;

      let w = 240,
        h = 180;
      c.width = w;
      c.height = h;

      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0, w, h);

      try {
        setDetectLoading(true);
        const faces = await detector.detect(c);
        setFaceDetected(faces.length > 0);
      } finally {
        setDetectLoading(false);
      }
    }, 600);
  };

  // ------------------------------------------------------------
  // Capture photo
  // ------------------------------------------------------------
  const onTakePhoto = async () => {
    setTakingPhoto(true);

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
    ctx.restore();

    const imageData = canvas.toDataURL("image/png");

    stopCamera();

    const coords = await getPositionSafe();
    const address = await reverseGeocodeSafe(coords.lat, coords.lng);

    const punch = {
      id: Date.now(),
      photo: imageData,
      lat: coords.lat,
      lng: coords.lng,
      address,
      time: new Date().toISOString(),
    };

    setPunchHistory((prev) => [punch, ...prev]);
    setTakingPhoto(false);
  };

  // ------------------------------------------------------------
  // Get location
  // ------------------------------------------------------------
  async function getPositionSafe() {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          }),
        () => resolve({ lat: null, lng: null }),
        { timeout: 8000 }
      );
    });
  }

  async function reverseGeocodeSafe(lat, lng) {
    if (!lat || !lng) return "Location not available";

    setReverseLoading(true);

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      return data.display_name || "Address not found";
    } catch {
      return "Unable to fetch address";
    } finally {
      setReverseLoading(false);
    }
  }

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <div className="container p-3" style={{ maxWidth: 900 }}>
      <h3>Punch In</h3>

      {/* PUNCH IN BUTTON */}
      {!streamActive && (
        <button
          className="btn btn-primary btn-lg w-100 mb-3"
          onClick={async () => {
            await checkPermissions();

            if (cameraAllowed !== true || locationAllowed !== true) {
              setPermissionPopup(true);
              return;
            }

            openCameraPreview();
          }}
        >
          Punch In
        </button>
      )}

      {/* PERMISSION POPUP */}
      {permissionPopup && (
        <div className="alert alert-warning p-3">
          <h6 className="fw-bold">Camera & Location Required</h6>
          <p className="small">Enable them in Chrome site settings.</p>

          <button
            className="btn btn-primary w-100 mb-2"
            onClick={() => {
              window.location.href =
                "chrome://settings/content/siteDetails?site=" +
                window.location.origin;
            }}
          >
            Open Site Settings
          </button>

          <button
            className="btn btn-secondary w-100"
            onClick={() => setPermissionPopup(false)}
          >
            Close
          </button>
        </div>
      )}

      {/* CAMERA ERROR */}
      {cameraError && (
        <div className="alert alert-danger">
          {cameraError}
          <button
            className="btn btn-warning w-100 mt-2"
            onClick={openCameraPreview}
          >
            Retry Camera
          </button>
        </div>
      )}

      {/* ALWAYS MOUNTED VIDEO (SHOW/HIDE ONLY) */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: "100%",
          borderRadius: 10,
          transform: "scaleX(-1)",
          background: "#000",
          display: streamActive ? "block" : "none",
        }}
      />

      {/* CAMERA PREVIEW UI */}
      {streamActive && (
        <div className="card mt-3">
          <div className="card-body p-2">
            <div className="d-flex justify-content-between mb-2">
              <strong>Camera Preview</strong>
              <button className="btn btn-secondary btn-sm" onClick={stopCamera}>
                Cancel
              </button>
            </div>

            {/* FACE DETECTION STATUS */}
            <div className="mt-2">
              {faceSupported ? (
                <span
                  className={`badge ${
                    faceDetected ? "bg-success" : "bg-warning text-dark"
                  }`}
                >
                  {faceDetected ? "Face Detected" : "No Face Found"}
                </span>
              ) : (
                <span className="badge bg-secondary">Face Detection Unsupported</span>
              )}
            </div>

            {/* TAKE PHOTO BUTTON */}
            <button
              className="btn btn-success btn-lg w-100 mt-3"
              disabled={!faceDetected && faceSupported}
              onClick={onTakePhoto}
            >
              {takingPhoto ? "Capturing..." : "Take Photo"}
            </button>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: "none" }} />
      <canvas ref={detectCanvasRef} style={{ display: "none" }} />

      {/* HISTORY */}
      <div className="d-flex justify-content-between mt-4 mb-2">
        <h5>Punch History</h5>
        {punchHistory.length > 0 && (
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={() => setShowClearModal(true)}
          >
            Clear History
          </button>
        )}
      </div>

      {punchHistory.length === 0 && (
        <div className="alert alert-light text-center">No punches yet</div>
      )}

      <div className="d-grid gap-3">
        {punchHistory.map((p) => (
          <div className="card" key={p.id}>
            <div className="row g-0">
              <div className="col-4">
                <img
                  src={p.photo}
                  alt="Punch"
                  className="img-fluid rounded-start"
                  style={{ height: 100, objectFit: "cover" }}
                />
              </div>
              <div className="col-8">
                <div className="card-body py-2">
                  <div className="fw-bold">
                    {new Date(p.time).toLocaleString()}
                  </div>
                  <div className="small text-muted">
                    Lat: {p.lat ?? "—"} | Lng: {p.lng ?? "—"}
                  </div>
                  <div className="small mt-1">
                    <strong>Address:</strong> {p.address}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CLEAR MODAL */}
      {showClearModal && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Clear History</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowClearModal(false)}
                />
              </div>

              <div className="modal-body">
                Are you sure you want to clear all punch records?
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowClearModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    setPunchHistory([]);
                    setShowClearModal(false);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
